import React, { useState, useEffect } from 'react';
import { Phone, Mail, MessageSquare, ClipboardList, Send, Clock, AlertCircle, ShoppingCart, MapPin, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCustomers } from '../context/CustomerContext';
import { useAlert } from '../context/AlertContext';

const CustomerInteractions = ({ customerId }) => {
    const { t, i18n } = useTranslation();
    const { logInteraction, getInteractions, updateInteraction } = useCustomers();
    const { showAlert, showConfirm } = useAlert();
    const [interactions, setInteractions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!customerId) return;
        setLoading(true);
        setError(null);

        const unsubscribe = getInteractions(customerId, (data, err) => {
            if (err) {
                console.error("Interaction History Error:", err);
                setError(err);
            } else {
                setInteractions(data || []);
            }
            setLoading(false);
        });
        return unsubscribe;
    }, [customerId]);

    const handleUseCode = async (interactionId) => {
        if (!await showConfirm("Bu kodu kullanıldı olarak işaretlemek istiyor musunuz?", { title: 'Kodu Kullan', confirmText: 'Kullan', type: 'warning' })) return;
        try {
            await updateInteraction(interactionId, {
                status: 'used',
                usedAt: new Date().toISOString()
            });
        } catch (err) {
            console.error("Error updating interaction:", err);
            showAlert("İşlem kaydedilemedi.", "error");
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'Call': return <Phone size={14} />;
            case 'Email': return <Mail size={14} />;
            case 'Purchase': return <ShoppingCart size={14} />;
            case 'Visit': return <MapPin size={14} />;
            default: return <ClipboardList size={14} />;
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', height: '100%' }}>
            <div style={{ flex: 1, overflowY: 'auto' }}>
                <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '1rem' }}>{t('interactions.history')}</h3>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '2rem', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                        {t('common.loading')}
                    </div>
                ) : error ? (
                    <div className="card" style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: '1rem', fontSize: '0.8125rem', color: '#991b1b' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontWeight: 700 }}>
                            <AlertCircle size={16} /> {t('interactions.errorTitle')}
                        </div>
                        <p style={{ marginBottom: '8px' }}>{t('interactions.errorDesc')}</p>
                        <p>{t('interactions.indexError')}</p>
                    </div>
                ) : interactions.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                        {t('interactions.noInteractions')}
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                                    <th style={{ padding: '8px', color: 'var(--text-muted)' }}>{t('interactions.type')}</th>
                                    <th style={{ padding: '8px', color: 'var(--text-muted)' }}>{t('interactions.note')}</th>
                                    <th style={{ padding: '8px', color: 'var(--text-muted)', textAlign: 'right' }}>{t('interactions.date')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {interactions.map((it) => (
                                    <tr key={it.id} style={{
                                        borderBottom: '1px solid var(--border)',
                                        opacity: it.isOptimistic ? 0.6 : 1,
                                        background: it.isOptimistic ? 'rgba(var(--primary-rgb), 0.02)' : 'transparent',
                                        transition: 'all 0.3s ease'
                                    }}>
                                        <td style={{ padding: '12px 8px', verticalAlign: 'top' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}>
                                                {getIcon(it.type)} {t(`interactions.${it.type.toLowerCase()}`)}
                                            </div>
                                        </td>
                                        <td style={{ padding: '12px 8px', color: 'var(--text-main)', lineHeight: '1.4' }}>
                                            {it.content}
                                            {it.type === 'Purchase' && it.amount > 0 && (
                                                <span style={{ marginLeft: '8px', fontSize: '0.75rem', fontWeight: 700, color: 'var(--primary)', background: 'rgba(111,78,55,0.08)', padding: '2px 8px', borderRadius: '6px' }}>
                                                    {Number(it.amount).toLocaleString('tr-TR')} TL
                                                </span>
                                            )}
                                            {it.code && it.status === 'active' && (
                                                <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                    <span style={{ background: '#fef9c3', color: '#854d0e', padding: '4px 10px', borderRadius: '8px', fontWeight: 800, fontSize: '0.75rem', border: '1px solid #fde047' }}>
                                                        KOD: {it.code}
                                                    </span>
                                                    <button
                                                        onClick={() => handleUseCode(it.id)}
                                                        style={{
                                                            padding: '4px 12px',
                                                            fontSize: '0.7rem',
                                                            background: 'var(--primary)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            fontWeight: 700,
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        Kullanıldı Olarak İşaretle
                                                    </button>
                                                </div>
                                            )}
                                            {it.code && it.status === 'used' && (
                                                <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px', color: '#16a34a', fontSize: '0.75rem', fontWeight: 700 }}>
                                                    <CheckCircle2 size={14} /> Kullanıldı ({it.usedAt ? new Date(it.usedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''})
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ padding: '12px 8px', color: 'var(--text-muted)', textAlign: 'right', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                                            {it.timestamp ? (
                                                (() => {
                                                    const date = it.timestamp.toDate ? it.timestamp.toDate() : new Date(it.timestamp);
                                                    return (
                                                        <>
                                                            {date.toLocaleDateString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                            <div style={{ fontSize: '0.7rem' }}>
                                                                {date.toLocaleTimeString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
                                                            </div>
                                                        </>
                                                    );
                                                })()
                                            ) : (
                                                <span style={{ fontSize: '0.7rem' }}>{t('common.loading')}</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomerInteractions;
